import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import {
  getBillingPortalSession,
  type BillingPortalUnavailable,
  type SquadCreationBlockedResponse,
} from "@workspace/api-client-react";

// WI#13 (slice B3) — the consumer for the 402 that POST /api/squads has returned since WI#6
// slice 2. Until this existed the mutation had no onError at all, so a blocked create was a
// silent no-op: the modal just sat there.
//
// Deliberately NOT a generic "payment required" dialog — the two codes are different user
// situations and need different asks. `subscription_required` is someone who has never paid;
// `plan_limit_reached` is a paying Squad subscriber who wants a second squad.
const COPY: Record<
  SquadCreationBlockedResponse["code"],
  { title: string; cta: string }
> = {
  subscription_required: {
    title: "Creating a squad is a paid feature",
    cta: "See plans",
  },
  plan_limit_reached: {
    title: "You've used the squad your plan includes",
    cta: "Upgrade to Coach Pro",
  },
};

// #93 (Jordyn's gate) — the prompt is a real dialog: Escape and a click outside close it like Not
// now does, and focus moves into it on open so keyboard and screen-reader users land in it. The
// caller restores focus to whatever opened it (onClose).
//
// aria-modal="true" tells assistive technology that nothing outside the dialog is available, so it
// must be TRUE (Celeste's finding, Kayla's ruling): Tab and Shift+Tab wrap inside the panel and can
// never land on a control hidden behind the backdrop.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function PromptFrame({ titleId, onClose, children }: { titleId: string; onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  // Callers pass a fresh onClose each render; keep the latest without re-running the effects, so
  // focus moves in ONCE on open and is never pulled back from a button on a re-render.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close.current();
        return;
      }
      if (e.key !== "Tab" || !panel.current) return;
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const active = document.activeElement;
      e.preventDefault(); // we place focus ourselves, so it can never leave the panel
      if (items.length === 0) {
        panel.current.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const index = items.indexOf(active as HTMLElement);
      if (index === -1) {
        // On the panel itself (just opened) or somehow outside it.
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey) {
        (index === 0 ? last : items[index - 1]!).focus();
      } else {
        (index === items.length - 1 ? first : items[index + 1]!).focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) close.current();
      }}
    >
      <motion.div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal outline-none"
      >
        {children}
      </motion.div>
    </div>
  );
}

export function UpgradePrompt({
  blocked,
  onClose,
}: {
  blocked: SquadCreationBlockedResponse;
  onClose: () => void;
}) {
  const copy = COPY[blocked.code] ?? COPY.subscription_required;
  const titleId = useId();

  // #13 — an account that already pays changes plan in Billing, never by buying again: following
  // "Upgrade to Coach Pro" into new-buyer checkout created a second, unprorated subscription
  // (Mandy's Coach Pro case 2). The server says which applies (billingAction); the prompt never
  // offers checkout in that case.
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingIssue, setBillingIssue] = useState<{ message: string; manageUrl?: string } | null>(null);
  async function openBilling() {
    setBillingBusy(true);
    setBillingIssue(null);
    try {
      const session = await getBillingPortalSession();
      window.location.assign(session.url);
    } catch (err) {
      const body = (err as { data?: BillingPortalUnavailable | null }).data;
      setBillingIssue(
        body?.error
          ? { message: body.error, manageUrl: body.manageUrl }
          : { message: "Couldn't open billing. Check your connection and try again." },
      );
      setBillingBusy(false);
    }
  }

  // The server decides both the line and whether Billing is offered (planChangeMessage /
  // billingAction): until change plan is live, there is no upgrade action to offer.
  if (blocked.planChangeMessage !== undefined || blocked.billingAction === "manage_billing") {
    const canOpenBilling = blocked.billingAction === "manage_billing";
    return (
      <PromptFrame titleId={titleId} onClose={onClose}>
          <h2 id={titleId} className="font-display text-xl font-bold mb-2">{copy.title}</h2>
          <p className="text-muted-foreground text-sm mb-2">{blocked.error}</p>
          {blocked.planChangeMessage && <p className="text-muted-foreground text-sm mb-6">{blocked.planChangeMessage}</p>}
          {billingIssue && (
            <p role="status" className="text-sm mb-4">
              {billingIssue.message}
              {billingIssue.manageUrl && (
                <>
                  {" "}
                  <a href={billingIssue.manageUrl} className="underline" target="_blank" rel="noreferrer">
                    Open Apple subscriptions
                  </a>
                </>
              )}
            </p>
          )}
          {canOpenBilling ? (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={openBilling}
                disabled={billingBusy}
                className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {billingBusy ? "Opening billing..." : "Open Billing"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="w-full border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Close
            </button>
          )}
      </PromptFrame>
    );
  }

  return (
    <PromptFrame titleId={titleId} onClose={onClose}>
        <h2 id={titleId} className="font-display text-xl font-bold mb-2">{copy.title}</h2>
        {/* Server-authored message — it knows the actual plan and limit, so it stays the
            source of truth rather than being restated here and drifting. */}
        <p className="text-muted-foreground text-sm mb-6">{blocked.error}</p>

        {blocked.checkoutUrl ? (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Not now
            </button>
            <a
              href={blocked.checkoutUrl}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity text-center"
            >
              {copy.cta}
            </a>
          </div>
        ) : (
          // checkoutUrl is omitted when PEAKCOMMERCE_CHECKOUT_URL is unset. The server already
          // logs that as an error; surface it honestly here rather than rendering a dead button
          // that looks like it works.
          <>
            <p className="text-destructive text-sm mb-6">
              Checkout isn't available right now. This is on our side, not yours — please try
              again shortly.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Close
            </button>
          </>
        )}
    </PromptFrame>
  );
}
